<template>
  <el-dialog
    v-if="isVisible"
    class="serum-dbs-switch-dialog order-test"
    v-model="isVisible"
    :show-close="false"
    @close="close"
  >
    <template #header>
      <div class="dialog-header">
        <div class="dialog-header-left">
          <img
            src="@/assets/MyPatient/input_order.svg"
            alt="Order"
            class="dialog-header-icon"
          >
          <span class="dialog-header-title">{{ title }}</span>
        </div>
        <button
          class="dialog-close-btn"
          type="button"
          @click="close"
        >
          <img
            src="@/assets/MyPatient/close.svg"
            alt="Close"
            class="dialog-close-icon"
          >
        </button>
      </div>
    </template>
    <div class="dialog-content-container">
      <div v-if="discardList.length > 0">
        <p class="dialog-content-text">{{ discardItemsText }}</p>
        <AffectedItemsTable from="Serum Price" to="DBS Price" :data="discardList">
          <template #header>
            <span class="ml-2 flex">
              {{ $t("OrderPage[\"Unsupported Tests\"]") }}
              <img 
                src="@/assets/orderTest/order-page/order-test-panel/menu-switch-dialog/triangle_alert_12px.svg" 
                class="pl-0.5"
              >
            </span>
          </template>
        </AffectedItemsTable>
      </div>
      <hr 
        v-if="discardList.length > 0 && differentPriceList.length > 0" 
        class="divider-line mt-3.5 mb-3"
      >
      <div v-if="differentPriceList.length > 0">
        <p class="dialog-content-text">{{ differentPriceItemsText }}</p>
        <AffectedItemsTable from="Serum Price" to="DBS Price" :data="differentPriceList">
          <template #header>
            <span class="ml-2 flex">{{ $t("OrderPage[\"Affected Tests\"]") }}</span>
          </template>
        </AffectedItemsTable>      
      </div>
      <div class="flex justify-between mt-9">
        <el-button class="cancel-btn" @click="close">{{ $t("OrderPage[\"Cancel\"]") }}</el-button>
        <el-button class="confirm-btn" @click="proceed">{{ $t("OrderPage[\"Proceed\"]") }}</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import AffectedItemsTable from "./AffectedItemsTable.vue";
import { mapState, mapGetters } from "vuex";
import { addSelectedDiscountPanel, removeSelectedDiscountPanel } from '@/utils/va-order-test/testAndPanelSelection/discountPanelSelection';
import { addSelectedXChooseYPanel, removeSelectedXChooseYPanel } from '@/utils/va-order-test/testAndPanelSelection/xChooseYPanelSelection';
import { removeSelectedCustomPanel } from '@/utils/va-order-test/testAndPanelSelection/customPanelSelection';
import { addSelectedSeasonalPromo, removeSelectedSeasonalPromo } from '@/utils/va-order-test/testAndPanelSelection/seasonalPromoSelection';
import { addSelectedTest, removeSelectedTest } from '@/utils/va-order-test/testAndPanelSelection/testSelection';
import { removeSelectedShortcut } from "@/utils/va-order-test/testAndPanelSelection/shortcutSelection.js"
import { handleDifferentAndSamePriceList, handleDiscardList } from ".";

export default {
  components: {
    AffectedItemsTable
  },
  props: {
    isShowDbsDialog: {
      type: Boolean,
      default: false
    },
    discardList: {
      type: Array,
      default: () => []
    },
    differentPriceList: {
      type: Array,
      default: () => []
    },
    samePriceList: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      isVisible: false,
    }
  },

  computed: {
    ...mapState("orderTest", ["isDbsVersion"]),
    ...mapState("orderTest/promotions", ["selectedSeasonalPromoList"]),
    ...mapGetters("orderTest", ["orderTestSupportObj"]),
    ...mapGetters("orderTest/tests", ["testList"]),
    ...mapGetters("orderTest/panels", ["bundleList"]),

    title() {
      return this.isDbsVersion 
        ? this.$t("OrderPage[\"Leaving At-Home Self Collect Menu\"]") : this.$t("OrderPage[\"Going to At-Home Menu\"]");
    },

    discardItemsText() {
      return this.isDbsVersion 
        ? this.$t("OrderPage[\"The below product(s) are not offered under the regular menu.\"]")
        : this.$t("OrderPage[\"The below product(s) are not offered under the At-Home self-collect menu. It requires a blood draw and all your selection below will be discarded should you choose to proceed.\"]");
    },

    differentPriceItemsText() {
      return this.isDbsVersion
        ? this.$t("OrderPage[\"The below product(s) are offered under our regular menu. It requires a blood draw and all your current selection below will be kept should you choose to proceed.\"]")
        : this.$t("OrderPage[\"The below product(s) are offered under our At-Home self-collect menu. You can order and provide convenience to your patients. All your current selection below will be kept should you choose to proceed.\"]")
    }

  },

  watch: {
    isShowDbsDialog(newVal) {
      this.isVisible = newVal;
    },
  },

  methods: {
    close() {
      this.$emit("closeWithCancel");
    },

    proceed() {
      // remove all discard tests
      handleDiscardList(this.discardList, this.orderTestSupportObj);
      // replace all different & same price tests
      handleDifferentAndSamePriceList(this.differentPriceList, this.samePriceList, this.orderTestSupportObj);
      this.$emit("closeWithProceed");
    },
  },
}
</script>

<style scoped>
.serum-dbs-switch-dialog :deep(.el-dialog) {
  border-radius: 10px;
  overflow: hidden;
}

.serum-dbs-switch-dialog :deep(.el-dialog__header) {
  padding: 0;
}

.serum-dbs-switch-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.dialog-header {
  height: 64px;
  background: #004879;
  color: #fff;
  border-radius: 10px 10px 0 0;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dialog-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dialog-header-icon {
  width: 24px;
  height: 24px;
  filter: brightness(0) invert(1);
}

.dialog-header-title {
  font-size: 20px;
  line-height: 30px;
  font-weight: 600;
}

.dialog-close-btn {
  border: none;
  background: transparent;
  width: 28px;
  height: 28px;
  cursor: pointer;
  padding: 0;
}

.dialog-close-icon {
  width: 100%;
  height: 100%;
}

.dialog-content-container {
  padding: 20px 24px 24px;
}

.dialog-content-text {
  word-break: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
}

.cancel-btn {
  height: 42px;
  width: 99px;;
  border-radius: 4px;
  background: #E6E6E6;
  color: #000000;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.43px;
}

.cancel-btn:hover {
  color: #000000;
}

.confirm-btn {
  height: 42px;
  width: 188px;
  border-radius: 4px;
  background-color: #20A8C3;
  color: #FFFFFF;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.43px;
}

.divider-line {
  width: 100%;
  height: 0px;
  border: 1px solid #E6E6E6;
}
</style>

<style>
.new-order-dialog .el-dialog.serum-dbs-switch-dialog {
  height: auto;
}

.new-order-dialog .el-dialog.serum-dbs-switch-dialog .el-dialog__body {
  flex: none;
}
</style>